/**
 * @file Parts-as-path state computation for the course-landing surface.
 * @module @labcraft/lab/ui/lib/parts-path
 *
 * Umbrella #927 / part #940: the course landing renders a course's
 * Parts as a path with four states — `done · now · next · locked`
 * (marker + word + rail color, never color alone). This module is the
 * pure derivation; the page renders the result with the `--obj-*`
 * tokens.
 *
 * **Pass-state model (umbrella #1446 Part B).** A Part is **done** when
 * its assessment (Part assessment) is passed — the authoritative
 * `passedPartIds` set, read from `learner_part_assessment` and delivered
 * by the auth-scoped `GET /api/progress/:courseId` surface. The pre-#1446
 * pointer heuristic (every Part ordered before the current one) is kept as
 * an ADDITIVE fallback so multi-Part position-based progress still reads
 * while pass rows accrue, and so a role-less course with no pass-state is
 * unaffected. The union is what lets a **single-Part** course reach `done`
 * (nothing is ordered "before" the only Part) — the fix for the completion
 * loop, where the current/last Part could never be `done` and clicking it
 * re-resumed the last chapter forever.
 *
 *   - **done** — assessment passed (`passedPartIds`), OR ordered before the
 *     current Part. Checked first, so a passed *current* Part reads `done`,
 *     not `now`.
 *   - **now** — the current Part, when not already `done`.
 *   - **locked** — a later Part with an unmet `prereq` (not in the done set).
 *   - **next** — any other Part (all prereqs done).
 *
 * With no pass-state and no `currentChapter` in this course, nothing is
 * done: entry Parts (no prereqs) are `next`, gated Parts `locked`.
 *
 * Free function (not a class method) mirroring the sibling pure helper
 * `lib/landing.ts` — the lab SPA's lib layer is where render-time
 * derivations live.
 */

import type { PartListing } from '../../shared/routes.ts'

/** One Part's place on the path. Drives the `--obj-{done,active,locked}` token + marker + word. */
export type PartPathState = 'done' | 'now' | 'next' | 'locked'

/**
 * A Part paired with its computed path state.
 *
 * @property part - The Part listing (carried through so the renderer
 *   has title/tier/chapters without a second lookup).
 * @property state - The Part's place on the path.
 */
export type PartPathEntry = {
  part: PartListing
  state: PartPathState
}

/**
 * Compute each Part's path state for a course.
 *
 * Parts are ordered by `number` (the result is in that order,
 * regardless of input order). `currentPartId` is the `partId` of the
 * learner's `currentChapter` **when it points into this course** —
 * pass `null` when the learner has no chapter pointer, or the pointer
 * is in a different course.
 *
 * @param parts - The course's Parts (any order; sorted internally).
 * @param currentPartId - Id of the Part the learner is currently on,
 *   or `null`.
 * @param passedPartIds - Ids of Parts whose assessment the learner has
 *   passed (umbrella #1446 Part B, from `GET /api/progress/:courseId`).
 *   Authoritative for `done`. Omit / empty to fall back to the pre-#1446
 *   pointer-only model (role-less courses, or before any pass exists).
 * @returns One {@link PartPathEntry} per Part, ordered by `number`.
 */
export function computePartsPath(
  parts: readonly PartListing[],
  currentPartId: string | null,
  passedPartIds?: readonly string[],
): PartPathEntry[] {
  const ordered = [...parts].sort((a, b) => a.number - b.number)
  const currentPart =
    currentPartId == null
      ? null
      : (ordered.find(p => p.id === currentPartId) ?? null)

  // Done = assessment passed (authoritative) UNION every Part ordered
  // strictly before the current one (the pre-#1446 pointer fallback). The
  // union lets a single-Part course reach `done` and reads a passed
  // *current* Part as done rather than looping on `now`.
  const doneIds = new Set<string>(passedPartIds ?? [])
  if (currentPart) {
    for (const p of ordered) {
      if (p.number < currentPart.number) doneIds.add(p.id)
    }
  }

  return ordered.map(part => {
    // `done` is checked before `now` so a passed current Part reads done.
    if (doneIds.has(part.id)) {
      return { part, state: 'done' }
    }
    if (currentPart && part.id === currentPart.id) {
      return { part, state: 'now' }
    }
    const blocked = part.prereqs.some(prereqId => !doneIds.has(prereqId))
    return { part, state: blocked ? 'locked' : 'next' }
  })
}
